import { observer } from "mobx-react-lite";
import { ItemInfoBlock, ItemInfoCopy } from "components";
import { useStores } from "stores";
import { CustomButton, WhiteBoxBlock } from "ui";
import { BankStatement, COMPANY_TYPES } from "interfaces";
import { getBankInfo, getCompanyInfo, getPaymentAmountInfo, STATEMENT_LABELS } from "./getColumns";
import { useEffect, useMemo, useState } from "react";
import { Alert, Divider, Flex, Spin, Typography } from "antd";
import { formatWithDecimals, requestWithNotify } from "utils";
import { CheckOutlined } from "@ant-design/icons";
import styles from "./buttonStatements.module.css";
import { useNotificationContext } from "providers";

export const BankStatementsBlock = observer(() => {
  const { bidCard, user, bankStatements } = useStores();

  const { openNotification } = useNotificationContext();

  useEffect(() => {
    if (!bidCard.bid?.id) return;

    bankStatements.setId(bidCard.bid.id);
    requestWithNotify({
      fetchFn: () => bankStatements.fetch(),
      openNotification,
      showErrorNotification: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidCard.bid.id]);

  return (
    <WhiteBoxBlock>
      <Flex justify="space-between" align="center" className="margin-bottom-xs">
        <Typography.Title level={2} className="margin-top-none">
          Платежные реквизиты
        </Typography.Title>
      </Flex>
      <Spin spinning={bankStatements.fetchStatus === "fetching"}>
        <Flex gap="large" align="stretch" className={styles.paymentBlockContainer}>
          {bankStatements.allData.map((details, index) => (
            <BankStatementItem
              key={`${details.paymentType}-${index}`}
              details={details}
              title={getBankStatementTitle(details.paymentType, user.roles)}
              id={`bank-statements-${details.paymentType.toLowerCase()}-${index}`}
            />
          ))}
          {bankStatements.fetchStatus === "error" && (
            <Alert message={"Ошибка"} description={bankStatements.fetchStatus} type="error" showIcon />
          )}
        </Flex>
      </Spin>
    </WhiteBoxBlock>
  );
});

type BankStatementProps = {
  details: BankStatement;
  title: string;
  id: string;
  isPaymentBlock?: boolean;
  bgColor?: string;
};

export const BankStatementItem = observer(
  ({ details, title, id, isPaymentBlock = true, bgColor = "bg-basic-200" }: BankStatementProps) => {
    const [copied, setCopied] = useState(false);

    const preparedValues = useMemo(() => {
      let allValues = [
        `${STATEMENT_LABELS.companyName}: ${details.companyName}`,
        `${details.type === COMPANY_TYPES.COMPANY ? "ОГРН" : "ОГРНИП"}: ${details.companyRegistrationNumber}`,
        `${STATEMENT_LABELS.inn}: ${details.inn}`,
        details.type === COMPANY_TYPES.COMPANY ? `${STATEMENT_LABELS.kpp}: ${details.kpp}` : "",
        `${STATEMENT_LABELS.bankName}: ${details.bankName}`,
        `${STATEMENT_LABELS.bik}: ${details.bik}`,
        `${STATEMENT_LABELS.correspondentAccount}: ${details.correspondentAccount}`,
        `${STATEMENT_LABELS.accountNumber}: ${details.accountNumber}`,
        `${STATEMENT_LABELS.paymentPurpose}: ${details.paymentPurpose}`,
        `${STATEMENT_LABELS.amount}: ${formatWithDecimals(details.rubAmount)}`,
      ];

      if (!isPaymentBlock) {
        allValues.splice(-2, 2);
      }

      return allValues.filter(Boolean).join("\n").trim();
    }, [details, isPaymentBlock]);

    const copyAllStatement = () => {
      navigator.clipboard.writeText(preparedValues).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      });
    };

    return (
      <WhiteBoxBlock id={id} className={`${styles.paymentDetailsBlock} ${bgColor}`}>
        <div className="flex-grow">
          <Typography.Title className="margin-top-none margin-bottom-sm" level={2}>
            {title}
          </Typography.Title>
          <Divider className="margin-top-none margin-bottom-sm" />
          <ItemInfoBlock items={getCompanyInfo(details)} oneColumn vertical={false} />
          <Divider className="margin-top-none margin-bottom-sm" />
          <div className="margin-bottom-sm">
            <Typography.Text className="font-weight-500">Банковские реквизиты получателя</Typography.Text>
          </div>
          <ItemInfoBlock items={getBankInfo(details)} oneColumn vertical={false} />
          {isPaymentBlock && (
            <>
              <Divider className="margin-top-none margin-bottom-sm" />
              <ItemInfoCopy
                title={STATEMENT_LABELS.paymentPurpose}
                text={details.paymentPurpose ?? ""}
                id="payment-purpose"
              />
              <ItemInfoBlock items={getPaymentAmountInfo(details)} oneColumn vertical={false} />
            </>
          )}
        </div>
        <div className={isPaymentBlock ? `${styles.paymentButtonContainer}` : `margin-top-lg`}>
          <CustomButton
            htmlType="button"
            type="default"
            icon={copied ? <CheckOutlined /> : null}
            iconPosition="end"
            onClick={copyAllStatement}
            className="full-width"
          >
            {copied ? "Скопировано" : "Скопировать реквизиты"}
          </CustomButton>
        </div>
      </WhiteBoxBlock>
    );
  }
);

const getBankStatementTitle = (paymentType: BankStatement["paymentType"], roles: { isOperator: boolean }): string => {
  if (roles.isOperator) {
    switch (paymentType) {
      case "FEE_CUSTOMER":
        return "Реквизиты для оплаты комиссии УВЭД";
      case "FEE_LP":
        return "Реквизиты для оплаты комиссии ПЛ";
      case "MAIN":
        return "Реквизиты для оплаты основной суммы";
    }
  }

  switch (paymentType) {
    case "FEE_CUSTOMER":
    case "FEE_LP":
      return "Реквизиты для оплаты комиссии";
    case "MAIN":
      return "Реквизиты для оплаты основной суммы";
  }
};
